import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  ChevronDown,
  Globe,
  TrendingUp,
  Shield,
  DollarSign,
  Users,
  Target,
  Award,
  Zap,
  MapPin,
} from 'lucide-react';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  icon: React.ReactNode;
  category: 'network' | 'pricing' | 'verification' | 'benefits';
}

const faqs: FAQ[] = [
  {
    id: '1',
    question: "You're starting in one city. Why should I join a single-city platform?",
    answer:
      "We're not a single-city platform - we're launching one city first, but we're already in talks with communities across multiple states. When you join as an approved vendor here, you're not just serving one city - you're securing early access to a growing national network. As we deploy in new cities, you'll have the option to expand your service area without reapplying. Think of this as getting in on the ground floor before the network scales to millions of residents.",
    icon: <Globe className="h-5 w-5" />,
    category: 'network',
  },
  {
    id: '2',
    question: 'What is the Statewide Network and how does it help my business?',
    answer:
      "The Statewide Network connects approved vendors across all major cities in a state. Once a statewide network is live, you'll be discoverable to millions of residents statewide - not just one city. If you're a solar installer serving one city today, you could win a statewide group contract tomorrow with thousands of homes across multiple cities. Same verification, exponentially larger customer base.",
    icon: <MapPin className="h-5 w-5" />,
    category: 'network',
  },
  {
    id: '3',
    question: 'How does the Multi-State and National Network expansion work?',
    answer:
      'As cities in neighboring states join, we create Multi-State Networks with cross-state commerce. A Texas HVAC vendor could bid on Louisiana contracts. A Louisiana seafood distributor could serve Texas communities. Eventually, the National Network connects vendors to 250M+ residents across 500+ cities. Your monthly or annual fee unlocks access to ALL deployed cities - no additional charges as the network grows.',
    icon: <TrendingUp className="h-5 w-5" />,
    category: 'network',
  },
  {
    id: '4',
    question: 'Why is there a verification fee?',
    answer:
      "This isn't a subscription fee - it's a marketing budget. Most businesses spend $500-2,000/month on Google Ads, Facebook campaigns, or door-to-door flyers. Our enterprise-grade verification (same system used by global payment platforms processing billions in transactions) costs us real money to operate - 30,000+ data sources, biometric authentication, AML screening across 11,000+ watchlists. The monthly fee covers verification infrastructure, in-app marketing, service request notifications, and platform maintenance. Compare that to traditional marketing costs, and you're saving 90%+ while reaching verified, high-intent customers.",
    icon: <DollarSign className="h-5 w-5" />,
    category: 'pricing',
  },
  {
    id: '5',
    question: 'Is this just another directory listing service?',
    answer:
      "No. This is a verified marketplace with authenticated residents and businesses. Directory sites like Yelp, Angi, or Thumbtack charge $300-500/month with zero verification (anyone can create fake reviews). We verify BOTH sides - vendors undergo enterprise KYB/KYC, residents verify identity with government-issued ID and biometric matching. When a resident requests your service, you know they're a real person in a real home, not a bot or scammer. This is commerce infrastructure, not a lead generation spam engine.",
    icon: <Shield className="h-5 w-5" />,
    category: 'verification',
  },
  {
    id: '6',
    question: "What happens after I'm verified?",
    answer:
      "Once verification completes, you're discoverable in the in-app vendor marketplace with a Verified Business badge. Residents can browse by category, search by service type, and filter by zone. You receive push notifications when residents post service requests in your area. You can submit proposals, message customers directly, and close contracts - all in-app. No more cold calling, door knocking, or paying for leads that go nowhere. You show up verified, trusted, and ready to serve.",
    icon: <Award className="h-5 w-5" />,
    category: 'benefits',
  },
  {
    id: '7',
    question: 'Can I offer group discounts to entire communities?',
    answer:
      "Absolutely - this is one of the biggest opportunities. Group buying is built into the platform. If you're a gym owner, you can offer 'Join with 50+ residents from your community and get 40% off memberships.' A lawn care company can bid on 200-home recurring contracts. An oil change shop can offer bulk discounts (20% off if 500 residents sign up). These aren't possible with traditional marketing - you'd never reach critical mass. Here, residents organize themselves and come to you ready to buy at scale.",
    icon: <Users className="h-5 w-5" />,
    category: 'benefits',
  },
  {
    id: '8',
    question: 'Where does the 0.4% transaction fee go?',
    answer:
      'Of the payment processing fees you pay, 0.4% stays local: 0.2% goes to the Community Treasury (resident-controlled infrastructure fund) and 0.2% goes to the city as additional municipal revenue. This creates a wealth-building cycle: when residents shop through verified vendors, they generate treasury funds that get spent on local infrastructure projects using verified vendors. Example: You process $100K in sales → $40 goes to local funds (Community Treasury + City) → Residents vote to fund a $150K park renovation → You bid on that contract and win it. The money your transactions contributed cycles back to you as contract opportunities.',
    icon: <DollarSign className="h-5 w-5" />,
    category: 'pricing',
  },
  {
    id: '9',
    question: 'Do I need to verify every time I join a new city?',
    answer:
      "No - one-time verification unlocks access to the entire network. Once you're approved in one city, you're approved for all future cities we deploy. If we launch in a new city next month, you simply update your service areas in settings and start receiving requests from residents. No re-verification, no additional onboarding. This is why getting in early matters - you're building a position in a network that's expanding nationwide and eventually globally. This isn't just national infrastructure - it's a verified global trustnet of commerce that enables vendors to acquire customers in larger volumes than otherwise possible.",
    icon: <Zap className="h-5 w-5" />,
    category: 'verification',
  },
  {
    id: '10',
    question: 'How many other vendors will be in my category?',
    answer:
      "We don't cap vendor categories artificially. If there are 50 HVAC companies in a city, all 50 can join if they verify. This isn't an exclusive directory - it's a marketplace. Competition drives quality and pricing, which benefits residents and attracts more users to the platform. Your advantage comes from your service quality, customer reviews, response time, and competitive pricing - not from artificial scarcity. The best vendors win the most contracts, period.",
    icon: <Target className="h-5 w-5" />,
    category: 'benefits',
  },
  {
    id: '11',
    question: 'What if I want to cancel later?',
    answer:
      "Monthly plans can be canceled anytime - you'll have access through the end of your current billing period, then the subscription stops. Annual plans are prepaid and non-refundable (you paid upfront for the year), but you can choose not to renew after 12 months. First month is non-refundable on all plans once verification completes due to third-party verification costs. No free trial, no money-back guarantee - this is a professional business network, not a consumer app.",
    icon: <DollarSign className="h-5 w-5" />,
    category: 'pricing',
  },
  {
    id: '12',
    question: 'Why should I trust this will actually grow beyond one city?',
    answer:
      "We're not a startup hoping for traction - we're building critical civic infrastructure with a proven economic model. Cities save 30-40% on platform costs by joining statewide networks, and they generate tax revenue from vendor marketplace activity. We've already documented statewide, multi-state, and national network pricing structures with cities across multiple states. This isn't speculative - it's a scalable business model backed by real municipal partnerships. Your early investment secures your position in a network designed to reach 250M+ residents.",
    icon: <TrendingUp className="h-5 w-5" />,
    category: 'network',
  },
];

export function VendorFAQSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section id="vendor-faq" className="py-16 sm:py-20 bg-gradient-to-b from-white to-muted/30">
      <div className="container px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <Badge className="mb-4">Vendor Questions</Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              What Vendors Ask Before Joining
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              We're not just building a platform for one city. We're building national commerce
              infrastructure. Here's what you need to know.
            </p>
          </div>

          {/* FAQ Grid - Two Columns on Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {faqs.map((faq) => {
              const isOpen = openId === faq.id;

              return (
                <div
                  key={faq.id}
                  className="rounded-lg border-2 border-border bg-card shadow-sm transition-all duration-200 hover:border-primary/50 cursor-pointer"
                  onClick={() => toggleFAQ(faq.id)}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                        {faq.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Question Header */}
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-sm font-semibold text-foreground leading-tight pr-1">
                            {faq.question}
                          </h3>
                          <ChevronDown
                            className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </div>

                        {/* Answer (Collapsible) */}
                        {isOpen && (
                          <div className="mt-3 animate-in fade-in slide-in-from-top-1 duration-300">
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
